import type { UIMessage } from 'ai';
import { AI_DURABLE_RUN_COMPLETION_METADATA_KEY, isAIDurableRunCompletionReason } from '@jms/model';
import type { AIDurableRunCompletion, AIDurableRunCompletionReason } from '@jms/model';
import type { RunEnding, CardExpiryInput } from './agent.run.ending.types';

/**
 * How a durable run ended, read from the completion record the runtime writes onto the final
 * assistant message (`workbuddyCompletion`). Pure helpers shared by the durable adapter (which
 * writes the record from a status read when the live stream ends) and the message list (which
 * renders the ending marker).
 */

/** Cards expire this long after suspension; mirrors `WORKBUDDY_AGENT_CARD_EXPIRY_MS` in jms-ai. */
export const AGENT_CARD_EXPIRY_MS = 60 * 60 * 1000;

const ENDING_LABELS: Record<RunEnding['reason'], string> = {
  stopped: 'Stopped',
  step_limit: 'Paused at the step limit. Send a message to continue.',
  time_limit: 'Paused at the time limit. Send a message to continue.',
  failed: 'Something went wrong and the run ended. What was done is kept above.',
  expired: 'The waiting request expired. Send a message to continue.',
  interrupted: 'Paused for maintenance. Send a message to carry on.',
};

function isRecord(value: unknown): value is Record<string, unknown> {

  return typeof value === 'object' && value !== null;
}

/** The completion record on a message, or null when the message carries none. */
export function readRunCompletion(message: Pick<UIMessage, 'role' | 'metadata'>): AIDurableRunCompletion | null {

  if (message.role !== 'assistant' || !isRecord(message.metadata)) return null;
  const record = message.metadata[AI_DURABLE_RUN_COMPLETION_METADATA_KEY];
  if (!isRecord(record) || !isAIDurableRunCompletionReason(record['reason'])) return null;
  return {
    schemaVersion: 1,
    reason: record['reason'],
    finishReason: typeof record['finishReason'] === 'string' ? record['finishReason'] : '',
    stepCount: typeof record['stepCount'] === 'number' ? record['stepCount'] : 0,
    endedAt: typeof record['endedAt'] === 'string' ? record['endedAt'] : '',
  };
}

/**
 * A run that failed before it wrote any reply. The server gives it its own empty "didn't finish"
 * message so the failure sits under that turn rather than on an earlier one; with nothing above it,
 * "what was done is kept above" would point at the previous answer.
 */
const EMPTY_FAILED_LABEL = 'This reply didn\'t finish. Send a message to try again.';

/** A completed run renders nothing extra; every other reason gets a small marker under the message. */
export function describeRunEnding(message: Pick<UIMessage, 'role' | 'metadata'> & { parts?: UIMessage['parts'] }): RunEnding | null {

  const completion = readRunCompletion(message);
  if (!completion || completion.reason === 'completed') return null;
  const failedWithoutReply = completion.reason === 'failed' && (message.parts?.length ?? 0) === 0;
  return { reason: completion.reason, label: failedWithoutReply ? EMPTY_FAILED_LABEL : ENDING_LABELS[completion.reason] };
}

/** Builds the record the adapter writes when it learns the reason from a status read rather than storage. */
export function buildRunCompletion(reason: AIDurableRunCompletionReason, finishReason: string, endedAtMs: number): AIDurableRunCompletion {

  return { schemaVersion: 1, reason, finishReason, stepCount: 0, endedAt: new Date(endedAtMs).toISOString() };
}

/** Returns a copy of the message carrying the completion record. */
export function withRunCompletion(message: UIMessage, completion: AIDurableRunCompletion): UIMessage {

  const metadata = isRecord(message.metadata) ? message.metadata : {};
  return { ...message, metadata: { ...metadata, [AI_DURABLE_RUN_COMPLETION_METADATA_KEY]: completion } } as UIMessage;
}

/**
 * Whether the waiting card should render as expired: the status route already finalized it, or its
 * suspension is older than the card window (the browser reaches the same verdict the server will on
 * its next read, so an aged card never looks live).
 */
export function isCardExpired(input: CardExpiryInput): boolean {

  if (input.state === 'expired') return true;
  if (input.expiresAt) {
    const expiresAtMs = Date.parse(input.expiresAt);
    if (Number.isFinite(expiresAtMs)) return input.nowMs > expiresAtMs;
  }
  if (input.suspendedAt) {
    const suspendedAtMs = Date.parse(input.suspendedAt);
    if (Number.isFinite(suspendedAtMs)) return input.nowMs - suspendedAtMs > AGENT_CARD_EXPIRY_MS;
  }
  return false;
}
